import {
  ENGAGED_MIN_PAGEVIEWS,
  ENGAGED_MIN_SECONDS,
  fmtInt,
  fmtPct,
  signal,
  type Filter,
  type Signal,
  type Stats,
} from "../../web-analytics";

type Props = {
  stats: Stats;
  filters: Filter[];
  moneyPaths: string[];
  /** Toggles the dim's `:1` filter (pickFilter), as a history push. */
  onPick: (dim: string, value: string) => void;
  /** Opens site settings; absent for someone who cannot edit them. */
  onSetUp?: () => void;
};

/**
 * Intent: how many VISITORS reached a money page, and how many were engaged.
 * Ported from Sonar's SignalsPanel, which counts sessions; NOLA counts
 * visitors (visitor_hash) so these sit beside the strip's Visitors tile.
 * Each number filters the page to those visitors, and clears it again.
 */
export default function SignalsPanel({ stats, filters, moneyPaths, onPick, onSetUp }: Props) {
  const money = signal(stats, filters, "money");
  const engaged = signal(stats, filters, "engaged");
  const paths = moneyPaths.join(", ");
  return (
    <section className="panel wa-signals" aria-label="Intent signals">
      {moneyPaths.length ? (
        <Stat s={money} label="Money-page visitors" caption={paths} captionTitle={paths} onPick={onPick} />
      ) : (
        <div className="wa-signal wa-signal-empty">
          <span className="wa-signal-label">Money-page visitors</span>
          <p>No money pages are set for this site. They mark buying intent, such as a quote or booking page.</p>
          {onSetUp ? (
            <button type="button" className="button secondary" onClick={onSetUp}>Set money pages</button>
          ) : (
            <p className="wa-signal-caption">An admin can set them in site settings.</p>
          )}
        </div>
      )}
      <Stat
        s={engaged}
        label="Engaged visitors"
        caption={`${ENGAGED_MIN_PAGEVIEWS}+ pages or ${ENGAGED_MIN_SECONDS / 60}+ minutes in one visit`}
        onPick={onPick}
      />
    </section>
  );
}

function Stat({ s, label, caption, captionTitle, onPick }: { s: Signal; label: string; caption: string; captionTitle?: string; onPick: Props["onPick"] }) {
  return (
    <button
      type="button"
      className={s.active ? "wa-signal active" : "wa-signal"}
      aria-pressed={s.active}
      aria-label={`${fmtInt(s.visitors)} ${label.toLowerCase()}. ${s.active ? "Remove this filter" : "Filter the page to these visitors"}`}
      onClick={() => onPick(s.dim, "1")}
    >
      <span className="wa-signal-label">{label}</span>
      <span className="wa-signal-value">
        <strong>{fmtInt(s.visitors)}</strong>
        {/* The share is left out while its own filter is on: the headline
            then only counts these visitors, so it would always read 100%. */}
        {s.share !== null && <span className="wa-signal-share">{fmtPct(s.share)} of visitors</span>}
      </span>
      <span className="wa-signal-caption" title={captionTitle}>{s.active ? "Filtered to these visitors. Click to clear." : caption}</span>
    </button>
  );
}
